import {useContext, useEffect, useRef, useState} from 'react';
import {TasksService} from '../../../../services/openapi/services/TasksService';
import {ArbeitszeitService} from '../../../../services/openapi/services/ArbeitszeitService';
import {UserService} from '../../../../services/openapi/services/UserService';
import type {TaskCreate} from '../../../../services/openapi/models/TaskCreate';
import type {Task} from '../../../../services/openapi/models/Task';
import {TaskStatus} from '../../../../services/openapi/models/TaskStatus';
import type {BauvorhabenWithID} from '../../../../services/openapi/models/BauvorhabenWithID';
import type {Contact} from '../../../../services/openapi/models/Contact';
import {IKunde} from '../Hausverwaltung/interfaces';
import UserContext from '../../../../Contexts/UserContext/UserContext';
import APIWrapperContext from '../../../../services/APIWrapperContext';
import {DatePicker} from '@mui/x-date-pickers/DatePicker';
import {LocalizationProvider} from '@mui/x-date-pickers/LocalizationProvider';
import {AdapterDayjs} from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import 'dayjs/locale/de';
import ImageCrop from './task-image-crop';

import {
    Alert,
    Autocomplete,
    Avatar,
    Box,
    Button,
    Card,
    CardContent,
    Checkbox,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    Grid,
    IconButton,
    Menu,
    MenuItem,
    Stack,
    styled,
    TextField,
    Typography,
} from '@mui/material';
import {
    Archive as ArchiveIcon,
    Article as WordIcon,
    AttachFile as AttachFileIcon,
    Block as BlockIcon,
    Business as BusinessIcon,
    CalendarToday as CalendarIcon,
    CheckCircle as CheckCircleIcon,
    Close as CloseIcon,
    CloudUpload as CloudUploadIcon,
    Code as CodeIcon,
    DataObject as JsonIcon,
    Description as DescriptionIcon,
    Image as ImageIcon,
    InsertDriveFile as DocumentIcon,
    Person as PersonIcon,
    PictureAsPdf as PdfIcon,
    PlayArrow as PlayArrowIcon,
    Schedule as ScheduleIcon,
    Slideshow as PowerPointIcon,
    TableChart as ExcelIcon,
    TextFields as TextIcon,
    Warning as WarningIcon,
} from '@mui/icons-material';
import TaskImageEdit from './task-image-edit';
import {AttachmentList} from './components/AttachmentList';

interface TaskFormProps {
    isOpen: boolean;
    onClose: () => void;
    onTaskCreated: () => void;
}

interface TaskFormData extends TaskCreate {
    rapport_allow?: boolean;
    // Projekt name for free text input (when not selecting from bauvorhaben list)
    projekt_name?: string;
    // Auftraggeber (client contact)
    auftraggeber_name?: string;
    auftraggeber_email?: string;
    auftraggeber_phone?: string;
    auftraggeber_address?: string;
    // Split address fields for new contacts
    auftraggeber_street?: string;
    auftraggeber_additional_street?: string;
    auftraggeber_postal_code?: string;
    auftraggeber_city?: string;
    // Terminvereinbarung (appointment contact)
    appointment_name?: string;
    appointment_email?: string;
    appointment_phone?: string;
    appointment_street?: string;
    appointment_additional_street?: string;
    appointment_postal_code?: string;
    appointment_city?: string;
    // Rechnungsempfänger (invoice recipient contact)
    invoice_name?: string;
    invoice_email?: string;
    invoice_phone?: string;
    invoice_street?: string;
    invoice_additional_street?: string;
    invoice_postal_code?: string;
    invoice_city?: string;
    // Full contact fields (textarea mode)
    auftraggeber_full_contact?: string;
    appointment_full_contact?: string;
    invoice_full_contact?: string;
    // Mode flags (true = textarea mode, false = structured mode)
    auftraggeber_use_full_contact?: boolean;
    appointment_use_full_contact?: boolean;
    invoice_use_full_contact?: boolean;
}

const ClickableField = styled(Box)(({theme}) => ({
    cursor: 'pointer',
    padding: theme.spacing(1),
    borderRadius: theme.shape.borderRadius,
    transition: 'background-color 0.2s',
    '&:hover': {
        backgroundColor: theme.palette.action.hover,
    },
}));

const HiddenInput = styled('input')({
    display: 'none',
});

export default function TaskForm({isOpen, onClose, onTaskCreated}: TaskFormProps) {
    const {user_roll, email} = useContext(UserContext);
    const {call_backend_api} = useContext(APIWrapperContext);
    const isAdmin = user_roll?.includes('Administrator') || false;

    const [formData, setFormData] = useState<TaskFormData>({
        title: '',
        description: '',
        rapport_allow: false,
        status: TaskStatus.OPEN,
        assigned_to: undefined,
        bauvorhaben_id: undefined,
        due_date: undefined,
        // Default to textarea mode for new tasks
        auftraggeber_use_full_contact: true,
        appointment_use_full_contact: true,
        invoice_use_full_contact: true,
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [users, setUsers] = useState<IKunde[]>([]);
    const [bauvorhaben, setBauvorhaben] = useState<BauvorhabenWithID[]>([]);
    const [contacts, setContacts] = useState<Contact[]>([]);
    const [loadingData, setLoadingData] = useState(true);
    const [selectedImages, setSelectedImages] = useState<File[]>([]);
    const [selectedDocuments, setSelectedDocuments] = useState<File[]>([]);
    const [uploadingImages, setUploadingImages] = useState(false);
    const [uploadingDocuments, setUploadingDocuments] = useState(false);
    const [statusMenuAnchor, setStatusMenuAnchor] = useState<null | HTMLElement>(null);
    const [assignmentMenuAnchor, setAssignmentMenuAnchor] = useState<null | HTMLElement>(null);
    const [selectedImage, setSelectedImage] = useState<File | null>(null);
    const [selectedImageIndex, setSelectedImageIndex] = useState<number | null>(null);

    const fileInputRef = useRef<HTMLInputElement>(null);

    const [editingImageIndex, setEditingImageIndex] = useState<number | null>(null);
    const [editedImages, setEditedImages] = useState<Map<number, File>>(new Map());

    const [resetWarningOpen, setResetWarningOpen] = useState(false);
    const [pendingEditIndex, setPendingEditIndex] = useState<number | null>(null);

    const [croppingImageIndex, setCroppingImageIndex] = useState<number | null>(null);
    const [croppingImageFile, setCroppingImageFile] = useState<File | null>(null);

    const [imageMenuAnchor, setImageMenuAnchor] = useState<{ [key: number]: HTMLElement | null }>({});
    const [documentMenuAnchor, setDocumentMenuAnchor] = useState<{ [key: number]: HTMLElement | null }>(
        {}
    );

    useEffect(() => {
        const fetchData = async () => {
            if (!isOpen) return;

            setLoadingData(true);
            setError(null);
            try {
                const [usersData, bauvorhabenData, contactsData] = await Promise.all([
                    isAdmin
                        ? UserService.listUsersWithMetadataUserListWithMetadataGet()
                        : Promise.resolve([]),
                    ArbeitszeitService.getAlleBauvorhabenArbeitzeitBauvorhabenGet(false),
                    TasksService.getAllContactsTaskContactsAllGet(),
                ]);
                setUsers(usersData.filter((u: IKunde) => u.userclass.includes('Administrator') || u.userclass.includes('Aufgaben Mitarbeiter')));
                setBauvorhaben(bauvorhabenData);
                setContacts(contactsData);
            } catch (err) {
                setError('Fehler beim Laden der Formulardaten');
                console.error('Error fetching form data:', err);
            } finally {
                setLoadingData(false);
            }
        };

        fetchData();
    }, [isOpen, isAdmin]);

    // Format contact as multi-line text for textarea
    const formatContactAsText = (contact: Contact): string => {
        const lines: string[] = [];
        if (contact.name) lines.push(contact.name);
        if (contact.email) lines.push(contact.email);
        if (contact.phone) lines.push(contact.phone);
        if (contact.address_text) lines.push(contact.address_text);
        return lines.join('\n');
    };

    // Handle contact selection for auftraggeber full contact
    const handleAuftraggeberContactSelect = (contact: Contact | null) => {
        if (contact) {
            setFormData((prev) => ({
                ...prev,
                auftraggeber_full_contact: formatContactAsText(contact),
                auftraggeber_name: contact.name || '',
                auftraggeber_email: contact.email || '',
                auftraggeber_phone: contact.phone || '',
            }));
        }
    };

    // Handle contact selection for appointment full contact
    const handleAppointmentContactSelect = (contact: Contact | null) => {
        if (contact) {
            setFormData((prev) => ({
                ...prev,
                appointment_full_contact: formatContactAsText(contact),
                appointment_name: contact.name || '',
                appointment_email: contact.email || '',
                appointment_phone: contact.phone || '',
            }));
        }
    };

    // Handle contact selection for invoice full contact
    const handleInvoiceContactSelect = (contact: Contact | null) => {
        if (contact) {
            setFormData((prev) => ({
                ...prev,
                invoice_full_contact: formatContactAsText(contact),
                invoice_name: contact.name || '',
                invoice_email: contact.email || '',
                invoice_phone: contact.phone || '',
            }));
        }
    };

    // Handle contact selection for structured mode (Einzelfelder) - Auftraggeber
    const handleAuftraggeberContactSelectStructured = (contact: Contact | null) => {
        if (contact) {
            const updates: any = {
                auftraggeber_name: contact.name || '',
                auftraggeber_email: contact.email || '',
                auftraggeber_phone: contact.phone || '',
            };
            if (contact.address_text) {
                const parts = contact.address_text.split(',').map(p => p.trim());
                if (parts.length >= 2) {
                    updates.auftraggeber_street = parts[0];
                    const plzCityMatch = parts[1].match(/^(\d{5})\s*(.*)$/);
                    if (plzCityMatch) {
                        updates.auftraggeber_postal_code = plzCityMatch[1];
                        updates.auftraggeber_city = plzCityMatch[2];
                    } else {
                        updates.auftraggeber_city = parts[1];
                    }
                } else {
                    updates.auftraggeber_street = contact.address_text;
                }
            }
            setFormData((prev) => ({ ...prev, ...updates }));
        }
    };

    // Handle contact selection for structured mode (Einzelfelder) - Appointment
    const handleAppointmentContactSelectStructured = (contact: Contact | null) => {
        if (contact) {
            const updates: any = {
                appointment_name: contact.name || '',
                appointment_email: contact.email || '',
                appointment_phone: contact.phone || '',
            };
            if (contact.address_text) {
                const parts = contact.address_text.split(',').map(p => p.trim());
                if (parts.length >= 2) {
                    updates.appointment_street = parts[0];
                    const plzCityMatch = parts[1].match(/^(\d{5})\s*(.*)$/);
                    if (plzCityMatch) {
                        updates.appointment_postal_code = plzCityMatch[1];
                        updates.appointment_city = plzCityMatch[2];
                    } else {
                        updates.appointment_city = parts[1];
                    }
                } else {
                    updates.appointment_street = contact.address_text;
                }
            }
            setFormData((prev) => ({ ...prev, ...updates }));
        }
    };

    // Handle contact selection for structured mode (Einzelfelder) - Invoice
    const handleInvoiceContactSelectStructured = (contact: Contact | null) => {
        if (contact) {
            const updates: any = {
                invoice_name: contact.name || '',
                invoice_email: contact.email || '',
                invoice_phone: contact.phone || '',
            };
            if (contact.address_text) {
                const parts = contact.address_text.split(',').map(p => p.trim());
                if (parts.length >= 2) {
                    updates.invoice_street = parts[0];
                    const plzCityMatch = parts[1].match(/^(\d{5})\s*(.*)$/);
                    if (plzCityMatch) {
                        updates.invoice_postal_code = plzCityMatch[1];
                        updates.invoice_city = plzCityMatch[2];
                    } else {
                        updates.invoice_city = parts[1];
                    }
                } else {
                    updates.invoice_street = contact.address_text;
                }
            }
            setFormData((prev) => ({ ...prev, ...updates }));
        }
    };

    const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files;
        if (!files || files.length === 0) return;

        const newImageFiles: File[] = [];
        const newDocumentFiles: File[] = [];

        Array.from(files).forEach((file) => {
            if (file.type.startsWith('image/')) {
                newImageFiles.push(file);
            } else {
                newDocumentFiles.push(file);
            }
        });

        if (newImageFiles.length > 0) {
            setSelectedImages((prev) => [...prev, ...newImageFiles]);
        }

        if (newDocumentFiles.length > 0) {
            setSelectedDocuments((prev) => [...prev, ...newDocumentFiles]);
        }
    };

    const handleImageSelect = handleFileUpload;

    const handleRemoveDocument = (index: number) => {
        setSelectedDocuments((prev) => prev.filter((_, i) => i !== index));
    };

    const handleDocumentClick = (document: File) => {
        const url = URL.createObjectURL(document);
        const linkElement = window.document.createElement('a');
        linkElement.href = url;
        linkElement.download = document.name;
        linkElement.click();
        URL.revokeObjectURL(url);
    };

    const getDocumentIcon = (mimeType: string, filename?: string) => {
        const extension = filename?.split('.').pop()?.toLowerCase() || '';

        if (mimeType.includes('pdf') || extension === 'pdf') {
            return <PdfIcon color='error'/>;
        }

        if (
            mimeType.includes('word') ||
            mimeType.includes('document') ||
            ['doc', 'docx'].includes(extension)
        ) {
            return <WordIcon color='primary'/>;
        }

        if (
            mimeType.includes('excel') ||
            mimeType.includes('spreadsheet') ||
            ['xls', 'xlsx', 'csv'].includes(extension)
        ) {
            return <ExcelIcon color='success'/>;
        }

        if (
            mimeType.includes('powerpoint') ||
            mimeType.includes('presentation') ||
            ['ppt', 'pptx'].includes(extension)
        ) {
            return <PowerPointIcon color='warning'/>;
        }

        if (mimeType.includes('text') || ['txt', 'md', 'readme'].includes(extension)) {
            return <TextIcon color='action'/>;
        }

        if (
            mimeType.includes('zip') ||
            mimeType.includes('rar') ||
            ['zip', 'rar', '7z', 'tar', 'gz'].includes(extension)
        ) {
            return <ArchiveIcon color='secondary'/>;
        }

        if (
            ['js', 'ts', 'tsx', 'jsx', 'py', 'java', 'c', 'cpp', 'css', 'html', 'php'].includes(extension)
        ) {
            return <CodeIcon color='info'/>;
        }

        if (['json', 'xml', 'yaml', 'yml'].includes(extension)) {
            return <JsonIcon color='info'/>;
        }

        if (mimeType.includes('image')) {
            return <ImageIcon color='primary'/>;
        }

        return <DocumentIcon color='action'/>;
    };

    const handleRemoveImage = (index: number) => {
        setSelectedImages((prev) => prev.filter((_, i) => i !== index));

        setEditedImages((prev) => {
            const newMap = new Map<number, File>();

            prev.forEach((file, oldIndex) => {
                if (oldIndex < index) {
                    newMap.set(oldIndex, file);
                } else if (oldIndex > index) {
                    newMap.set(oldIndex - 1, file);
                }
            });

            return newMap;
        });
    };

    const handleImageClick = (image: File, index: number) => {
        const imageToShow = editedImages.get(index) || image;
        setSelectedImage(imageToShow);
        setSelectedImageIndex(index);
    };

    const handleDownloadImage = (image: File, index: number) => {
        const imageToDownload = editedImages.get(index) || image;
        const url = URL.createObjectURL(imageToDownload);
        const linkElement = window.document.createElement('a');
        linkElement.href = url;
        linkElement.download = imageToDownload.name;
        linkElement.click();
        URL.revokeObjectURL(url);
    };

    const debugImageState = () => {
        console.log(
            'Selected Images:',
            selectedImages.map((img, idx) => ({index: idx, name: img.name}))
        );
        console.log(
            'Edited Images:',
            Array.from(editedImages.entries()).map(([index, file]) => ({index, name: file.name}))
        );
    };

    const uploadImages = async (taskId: number) => {
        setUploadingImages(true);
        try {
            console.log('=== UPLOAD IMAGES DEBUG ===');
            console.log('Task ID:', taskId);
            console.log('Total images to upload:', selectedImages.length);
            console.log('Edited images map:', editedImages);

            for (let i = 0; i < selectedImages.length; i++) {
                const originalImage = selectedImages[i];
                const editedImage = editedImages.get(i);
                const imageToUpload = editedImage || originalImage;

                console.log(`Uploading image ${i}:`, {
                    original: originalImage.name,
                    edited: editedImage?.name,
                    uploading: imageToUpload.name,
                    isEdited: !!editedImage,
                    size: imageToUpload.size,
                });

                const result = await TasksService.uploadTaskImageTaskTaskIdImagesPost(taskId, {
                    file: imageToUpload,
                });

                console.log(`Image ${i} upload result:`, result);
            }

            console.log('All images uploaded successfully');
            setSelectedImages([]);
            setEditedImages(new Map());
        } catch (err) {
            setError('Fehler beim Hochladen einiger Bilder');
            console.error('Error uploading images:', err);
            throw err;
        } finally {
            setUploadingImages(false);
        }
    };

    const uploadDocuments = async (taskId: number) => {
        setUploadingDocuments(true);
        try {
            console.log('=== UPLOAD DOCUMENTS DEBUG ===');
            console.log('Total documents to upload:', selectedDocuments.length);

            for (let i = 0; i < selectedDocuments.length; i++) {
                const document = selectedDocuments[i];

                console.log(`Uploading document ${i}:`, {
                    name: document.name,
                    type: document.type,
                    size: document.size,
                });

                await TasksService.uploadTaskDocumentTaskTaskIdDocumentsPost(taskId, {
                    file: document,
                });
            }

            console.log('All documents uploaded successfully');
            setSelectedDocuments([]);
        } catch (err) {
            setError('Fehler beim Hochladen einiger Dokumente');
            console.error('Error uploading documents:', err);
        } finally {
            setUploadingDocuments(false);
        }
    };

    const resetForm = () => {
        setFormData({
            title: '',
            description: '',
            rapport_allow: false,
            status: TaskStatus.OPEN,
            assigned_to: undefined,
            bauvorhaben_id: undefined,
            projekt_name: undefined,
            due_date: undefined,
            // Auftraggeber
            auftraggeber_name: undefined,
            auftraggeber_email: undefined,
            auftraggeber_phone: undefined,
            auftraggeber_address: undefined,
            auftraggeber_street: undefined,
            auftraggeber_additional_street: undefined,
            auftraggeber_postal_code: undefined,
            auftraggeber_city: undefined,
            // Terminvereinbarung
            appointment_name: undefined,
            appointment_email: undefined,
            appointment_phone: undefined,
            appointment_street: undefined,
            appointment_additional_street: undefined,
            appointment_postal_code: undefined,
            appointment_city: undefined,
            // Rechnungsempfänger
            invoice_name: undefined,
            invoice_email: undefined,
            invoice_phone: undefined,
            invoice_street: undefined,
            invoice_additional_street: undefined,
            invoice_postal_code: undefined,
            invoice_city: undefined,
            // Full contact fields
            auftraggeber_full_contact: undefined,
            appointment_full_contact: undefined,
            invoice_full_contact: undefined,
            // Mode flags - default to textarea mode
            auftraggeber_use_full_contact: true,
            appointment_use_full_contact: true,
            invoice_use_full_contact: true,
        });
        setSelectedImages([]);
        setSelectedDocuments([]);
        setEditedImages(new Map());
        setError(null);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        // Validate required Projekt field
        if (!formData.bauvorhaben_id && !formData.projekt_name) {
            setError('Bitte wählen Sie ein Projekt aus oder geben Sie einen Projektnamen ein.');
            return;
        }

        setLoading(true);
        setError(null);

        console.log('=== SUBMIT DEBUG ===');
        debugImageState();

        call_backend_api(
            TasksService.createTaskTaskPost(formData as TaskCreate),
            async (task: Task) => {
                console.log('Task created, starting uploads...');

                const uploadPromises = [];

                if (selectedImages.length > 0) {
                    console.log('Adding image upload promise');
                    uploadPromises.push(uploadImages(task.id));
                }

                if (selectedDocuments.length > 0) {
                    console.log('Adding document upload promise');
                    uploadPromises.push(uploadDocuments(task.id));
                }

                if (uploadPromises.length > 0) {
                    console.log('Waiting for uploads to complete...');
                    await Promise.all(uploadPromises);
                    console.log('All uploads completed');
                }

                console.log('Calling onTaskCreated and closing dialog');
                onTaskCreated();
                onClose();
                resetForm();
            },
            undefined,
            () => setLoading(false),
            true,
            'Aufgabe erfolgreich erstellt',
            'Fehler beim Erstellen der Aufgabe'
        );
    };

    const getStatusIcon = (status: TaskStatus | undefined) => {
        if (!status) return <ScheduleIcon/>;

        switch (status) {
            case TaskStatus.COMPLETED:
                return <CheckCircleIcon/>;
            case TaskStatus.IN_PROGRESS:
                return <PlayArrowIcon/>;
            case TaskStatus.BLOCKED:
                return <BlockIcon/>;
            default:
                return <ScheduleIcon/>;
        }
    };

    const getStatusColor = (status: TaskStatus | undefined) => {
        if (!status) return 'warning';

        switch (status) {
            case TaskStatus.COMPLETED:
                return 'success';
            case TaskStatus.IN_PROGRESS:
                return 'primary';
            case TaskStatus.BLOCKED:
                return 'error';
            default:
                return 'warning';
        }
    };

    const getStatusLabel = (status: TaskStatus | undefined) => {
        if (!status) return 'Offen';

        switch (status) {
            case TaskStatus.OPEN:
                return 'Offen';
            case TaskStatus.IN_PROGRESS:
                return 'In Bearbeitung';
            case TaskStatus.COMPLETED:
                return 'Abgeschlossen';
            case TaskStatus.BLOCKED:
                return 'Blockiert';
            default:
                return 'Offen';
        }
    };

    const handleConfirmReset = () => {
        if (pendingEditIndex !== null) {
            setEditedImages((prev) => {
                const newMap = new Map(prev);
                newMap.delete(pendingEditIndex);
                return newMap;
            });

            setEditingImageIndex(pendingEditIndex);
            setPendingEditIndex(null);
        }
        setResetWarningOpen(false);
    };

    const handleCancelReset = () => {
        setPendingEditIndex(null);
        setResetWarningOpen(false);
    };

    const handleSaveEditedImage = async (editedBlob: Blob) => {
        if (editingImageIndex !== null) {
            const originalImage = selectedImages[editingImageIndex];
            const editedFile = new File([editedBlob], `edited_${originalImage.name}`, {
                type: 'image/png',
            });

            setEditedImages((prev) => new Map(prev.set(editingImageIndex, editedFile)));
            setEditingImageIndex(null);
        }
    };

    const getImageUrl = (image: File, index: number) => {
        const editedImage = editedImages.get(index);
        return editedImage ? URL.createObjectURL(editedImage) : URL.createObjectURL(image);
    };

    const getImageForEditing = (index: number): File => {
        return editedImages.get(index) || selectedImages[index];
    };

    const getImageForViewing = (index: number): File => {
        return editedImages.get(index) || selectedImages[index];
    };

    const handleCropImage = (index: number) => {
        const imageFile = editedImages.get(index) || selectedImages[index];
        setCroppingImageFile(imageFile);
        setCroppingImageIndex(index);
    };

    const handleSaveCroppedImage = (croppedBlob: Blob) => {
        if (croppingImageIndex !== null) {
            const originalImage = selectedImages[croppingImageIndex];
            const croppedFile = new File([croppedBlob], `cropped_${originalImage.name}`, {
                type: 'image/png',
            });

            setEditedImages((prev) => new Map(prev.set(croppingImageIndex, croppedFile)));

            setCroppingImageIndex(null);
            setCroppingImageFile(null);
        }
    };

    const handleEditImage = (index: number) => {
        if (editedImages.has(index)) {
            setPendingEditIndex(index);
            setResetWarningOpen(true);
        } else {
            setEditingImageIndex(index);
        }
    };

    if (!isOpen) return null;

    return (
        <>
            <Dialog
                open={isOpen}
                onClose={onClose}
                maxWidth='lg'
                fullWidth
                PaperProps={{
                    sx: {
                        height: 'fit-content',
                        maxHeight: '90vh',
                        borderRadius: 3,
                        boxShadow: '0 16px 48px rgba(0,0,0,0.1)',
                        backgroundImage: 'linear-gradient(180deg, #ffffff 0%, #fafafa 100%)',
                    },
                }}>
                <DialogTitle sx={{
                    px: 3,
                    pb: 1,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
                }}>
                    <Box display='flex' justifyContent='space-between' alignItems='center'>
                        <Box display='flex' alignItems='center' gap={2}>
                            <Box
                                sx={{
                                    width: 40,
                                    height: 40,
                                    borderRadius: 2,
                                    backgroundColor: 'primary.50',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }}
                            >
                                <DescriptionIcon sx={{color: 'primary.main', fontSize: 20}}/>
                            </Box>
                            <Typography variant='h5' component='div' fontWeight={600} color='text.primary'>
                                Neue Aufgabe erstellen
                            </Typography>
                        </Box>
                        <IconButton
                            onClick={onClose}
                            sx={{
                                color: 'text.secondary',
                                '&:hover': {
                                    backgroundColor: 'action.hover',
                                    color: 'primary.main',
                                },
                            }}
                        >
                            <CloseIcon/>
                        </IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent sx={{
                    px: 3,
                    py: 2,
                    backgroundColor: '#fafafa',
                    backgroundImage: 'linear-gradient(180deg, #fafafa 0%, #ffffff 100%)',
                }}>
                    {error && (
                        <Alert severity='error' sx={{mb: 2}}>
                            {error}
                        </Alert>
                    )}

                    {loadingData ? (
                        <Box display='flex' justifyContent='center' alignItems='center' minHeight='400px'>
                            <CircularProgress size={60}/>
                        </Box>
                    ) : (
                        <Box component='form' onSubmit={handleSubmit}>
                            {/* Title */}
                            <Box sx={{mb: 3}}>
                                <TextField
                                    fullWidth
                                    required
                                    value={formData.title}
                                    onChange={(e) => setFormData({...formData, title: e.target.value})}
                                    variant='outlined'
                                    size='medium'
                                    placeholder='Titel der Aufgabe'
                                    sx={{
                                        '& .MuiInputBase-input': {
                                            fontSize: '2rem',
                                            fontWeight: 500,
                                        },
                                    }}
                                />
                            </Box>

                            {/* Status, Assignment, and Details Card */}
                            <Card variant='outlined' sx={{mb: 3}}>
                                <CardContent>
                                    <Grid container spacing={3}>
                                        {/* Left Column */}
                                        <Grid item xs={12} sm={6}>
                                            <Stack spacing={2}>
                                                {/* Status */}
                                                <Box>
                                                    <Typography variant='body2' color='text.secondary' sx={{mb: 1}}>
                                                        Status
                                                    </Typography>
                                                    <Box position='relative'>
                                                        <Chip
                                                            icon={getStatusIcon(formData.status)}
                                                            label={getStatusLabel(formData.status)}
                                                            color={getStatusColor(formData.status)}
                                                            size='medium'
                                                            onClick={(e) => setStatusMenuAnchor(e.currentTarget)}
                                                            sx={{cursor: 'pointer'}}
                                                        />
                                                        <Menu
                                                            anchorEl={statusMenuAnchor}
                                                            open={Boolean(statusMenuAnchor)}
                                                            onClose={() => setStatusMenuAnchor(null)}>
                                                            {Object.values(TaskStatus).map((status) => (
                                                                <MenuItem
                                                                    key={status}
                                                                    onClick={() => {
                                                                        setFormData({...formData, status});
                                                                        setStatusMenuAnchor(null);
                                                                    }}
                                                                    selected={formData.status === status}>
                                                                    <Box display='flex' alignItems='center' gap={1}>
                                                                        {getStatusIcon(status)}
                                                                        {getStatusLabel(status)}
                                                                    </Box>
                                                                </MenuItem>
                                                            ))}
                                                        </Menu>
                                                    </Box>
                                                </Box>

                                                {/* Assignment */}
                                                <Box>
                                                    <Typography variant='body2' color='text.secondary' sx={{mb: 1}}>
                                                        Verantwortliche
                                                    </Typography>
                                                    {isAdmin ? (
                                                        <Box position='relative'>
                                                            <ClickableField
                                                                onClick={(e) => setAssignmentMenuAnchor(e.currentTarget)}
                                                                display='flex'
                                                                alignItems='center'
                                                                gap={1}
                                                                sx={{ml: -1}}>
                                                                <Avatar sx={{bgcolor: 'primary.main', width: 32, height: 32}}>
                                                                    <PersonIcon sx={{fontSize: 20}}/>
                                                                </Avatar>
                                                                <Typography variant='body1'>
                                                                    {formData.assigned_to
                                                                        ? users.find((u) => u.user_id === formData.assigned_to)
                                                                        ?.fullname || 'Nicht zugewiesen'
                                                                        : 'Nicht zugewiesen'}
                                                                </Typography>
                                                            </ClickableField>
                                                            <Menu
                                                                anchorEl={assignmentMenuAnchor}
                                                                open={Boolean(assignmentMenuAnchor)}
                                                                onClose={() => setAssignmentMenuAnchor(null)}>
                                                                {users.map((user) => (
                                                                    <MenuItem
                                                                        key={user.user_id}
                                                                        onClick={() => {
                                                                            setFormData({...formData, assigned_to: user.user_id});
                                                                            setAssignmentMenuAnchor(null);
                                                                        }}
                                                                        selected={formData.assigned_to === user.user_id}>
                                                                        {user.fullname || user.email}
                                                                    </MenuItem>
                                                                ))}
                                                            </Menu>
                                                        </Box>
                                                    ) : (
                                                        <Box display='flex' alignItems='center' gap={1}>
                                                            <Avatar sx={{bgcolor: 'primary.main', width: 32, height: 32}}>
                                                                <PersonIcon sx={{fontSize: 20}}/>
                                                            </Avatar>
                                                            <Typography variant='body1'>{email}</Typography>
                                                        </Box>
                                                    )}
                                                </Box>
                                                {/** Rapport */}
                                                <Box>
                                                    <Typography variant='body2' color='text.secondary' sx={{mb: 1}}>
                                                        Rapport erstellen
                                                    </Typography>
                                                    <FormControlLabel
                                                        control={
                                                            <Checkbox
                                                                checked={formData.rapport_allow || false}
                                                                onChange={(e) =>
                                                                    setFormData({...formData, rapport_allow: e.target.checked})
                                                                }
                                                                color='primary'
                                                            />
                                                        }
                                                        label="Rapport erstellen"
                                                    />
                                                </Box>
                                            </Stack>
                                        </Grid>
                                        {/* Right Column */}
                                        <Grid item xs={12} sm={6}>
                                            <Stack spacing={2}>
                                                {/* Fälligkeitsdatum */}
                                                <Box>
                                                    <Typography variant='body2' color='text.secondary' sx={{mb: 1}}>
                                                        Fälligkeitsdatum
                                                    </Typography>
                                                    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale='de'>
                                                        <DatePicker
                                                            value={formData.due_date ? dayjs(formData.due_date) : null}
                                                            onChange={(newValue) => {
                                                                if (newValue && newValue.isValid()) {
                                                                    const dateTimeString = newValue
                                                                        .startOf('day')
                                                                        .format('YYYY-MM-DDTHH:mm:ss');
                                                                    setFormData({...formData, due_date: dateTimeString});
                                                                }
                                                            }}
                                                            slotProps={{
                                                                textField: {
                                                                    size: 'small',
                                                                    InputProps: {
                                                                        startAdornment: (
                                                                            <CalendarIcon color='action' sx={{fontSize: 20, mr: 1}}/>
                                                                        ),
                                                                        readOnly: true,
                                                                    },
                                                                },
                                                                actionBar: {
                                                                    actions: ['accept'],
                                                                },
                                                            }}
                                                            views={['year', 'month', 'day']}
                                                            openTo='day'
                                                            onError={(error) => {
                                                                if (error) {
                                                                    return;
                                                                }
                                                            }}
                                                        />
                                                    </LocalizationProvider>
                                                </Box>

                                                {/* Projekt - Required with Autocomplete freeSolo */}
                                                <Box>
                                                    <Typography variant='body2' color='text.secondary' sx={{mb: 1}}>
                                                        Projekt *
                                                    </Typography>
                                                    <Autocomplete
                                                        freeSolo
                                                        size='small'
                                                        options={bauvorhaben}
                                                        getOptionLabel={(option) =>
                                                            typeof option === 'string' ? option : option.bvh_name
                                                        }
                                                        value={
                                                            formData.bauvorhaben_id
                                                                ? bauvorhaben.find((bvh) => bvh.id === formData.bauvorhaben_id) || null
                                                                : formData.projekt_name || null
                                                        }
                                                        onChange={(_, newValue) => {
                                                            if (newValue === null) {
                                                                setFormData({
                                                                    ...formData,
                                                                    bauvorhaben_id: undefined,
                                                                    projekt_name: undefined
                                                                });
                                                            } else if (typeof newValue === 'string') {
                                                                setFormData({
                                                                    ...formData,
                                                                    bauvorhaben_id: undefined,
                                                                    projekt_name: newValue
                                                                });
                                                            } else {
                                                                setFormData({
                                                                    ...formData,
                                                                    bauvorhaben_id: newValue.id,
                                                                    projekt_name: undefined
                                                                });
                                                            }
                                                        }}
                                                        onInputChange={(_, newInputValue, reason) => {
                                                            if (reason === 'input') {
                                                                // Check if input matches an existing bauvorhaben
                                                                const matchingBvh = bauvorhaben.find(
                                                                    bvh => bvh.bvh_name.toLowerCase() === newInputValue.toLowerCase()
                                                                );
                                                                if (matchingBvh) {
                                                                    setFormData({
                                                                        ...formData,
                                                                        bauvorhaben_id: matchingBvh.id,
                                                                        projekt_name: undefined
                                                                    });
                                                                } else {
                                                                    setFormData({
                                                                        ...formData,
                                                                        bauvorhaben_id: undefined,
                                                                        projekt_name: newInputValue || undefined
                                                                    });
                                                                }
                                                            }
                                                        }}
                                                        renderInput={(params) => (
                                                            <TextField
                                                                {...params}
                                                                required
                                                                placeholder="Projekt wählen oder eingeben..."
                                                                error={!formData.bauvorhaben_id && !formData.projekt_name}
                                                                InputProps={{
                                                                    ...params.InputProps,
                                                                    startAdornment: (
                                                                        <>
                                                                            <BusinessIcon color='action' sx={{fontSize: 20, mr: 1}}/>
                                                                            {params.InputProps.startAdornment}
                                                                        </>
                                                                    ),
                                                                }}
                                                            />
                                                        )}
                                                        renderOption={(props, option) => (
                                                            <li {...props} key={option.id}>
                                                                <Typography variant="body2">{option.bvh_name}</Typography>
                                                            </li>
                                                        )}
                                                    />
                                                </Box>
                                            </Stack>
                                        </Grid>
                                    </Grid>
                                </CardContent>
                            </Card>

                            {/* Contacts Card */}
                            <Card variant='outlined' sx={{mb: 3}}>
                                <CardContent>
                                    <Box display='flex' alignItems='center' gap={1} mb={3}>
                                        <PersonIcon color='primary'/>
                                        <Typography variant='h6'>Kontaktdaten</Typography>
                                    </Box>
                                    <Grid container spacing={3}>
                                        {/* Auftraggeber - Left Column */}
                                        <Grid item xs={12} md={4}>
                                            <Box sx={{
                                                p: 2,
                                                border: '1px solid',
                                                borderColor: 'divider',
                                                borderRadius: 2,
                                                height: '100%',
                                            }}>
                                                <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2}}>
                                                    <Typography variant='subtitle1' fontWeight={600}>
                                                        Auftraggeber Kontaktdaten
                                                    </Typography>
                                                    <FormControlLabel
                                                        control={
                                                            <Checkbox
                                                                size='small'
                                                                checked={!formData.auftraggeber_use_full_contact}
                                                                onChange={(e) => {
                                                                    const useStructured = e.target.checked;
                                                                    if (!useStructured && !formData.auftraggeber_use_full_contact) {
                                                                        // Switching to textarea mode - auto-format existing data
                                                                        const parts = [
                                                                            formData.auftraggeber_name,
                                                                            formData.auftraggeber_email,
                                                                            formData.auftraggeber_phone,
                                                                            formData.auftraggeber_street,
                                                                            formData.auftraggeber_additional_street,
                                                                            [formData.auftraggeber_postal_code, formData.auftraggeber_city].filter(Boolean).join(' ')
                                                                        ].filter(Boolean);
                                                                        setFormData({
                                                                            ...formData,
                                                                            auftraggeber_use_full_contact: true,
                                                                            auftraggeber_full_contact: parts.join('\n') || formData.auftraggeber_full_contact
                                                                        });
                                                                    } else {
                                                                        setFormData({...formData, auftraggeber_use_full_contact: !useStructured});
                                                                    }
                                                                }}
                                                            />
                                                        }
                                                        label={<Typography variant='body2'>Einzelfelder</Typography>}
                                                        sx={{mr: 0}}
                                                    />
                                                </Box>
                                                <Stack spacing={2}>
                                                    {formData.auftraggeber_use_full_contact ? (
                                                        <Box>
                                                            <Autocomplete
                                                                options={contacts}
                                                                getOptionLabel={(option) => option.name}
                                                                onChange={(_, newValue) => handleAuftraggeberContactSelect(newValue)}
                                                                size="small"
                                                                renderInput={(params) => (
                                                                    <TextField
                                                                        {...params}
                                                                        label="Kontakt suchen"
                                                                        placeholder="Kontakt auswählen..."
                                                                        size="small"
                                                                        sx={{mb: 1}}
                                                                    />
                                                                )}
                                                                renderOption={(props, option) => (
                                                                    <li {...props} key={option.id}>
                                                                        <Box>
                                                                            <Typography variant="body2">{option.name}</Typography>
                                                                            {option.email && (
                                                                                <Typography variant="caption" color="text.secondary">
                                                                                    {option.email}
                                                                                </Typography>
                                                                            )}
                                                                        </Box>
                                                                    </li>
                                                                )}
                                                            />
                                                            <TextField
                                                                fullWidth
                                                                size='small'
                                                                label='Kontaktdaten'
                                                                value={formData.auftraggeber_full_contact || ''}
                                                                onChange={(e) =>
                                                                    setFormData({...formData, auftraggeber_full_contact: e.target.value})
                                                                }
                                                                variant='outlined'
                                                                multiline
                                                                rows={5}
                                                                placeholder={'Name\nE-Mail\nTelefon\nAdresse'}
                                                            />
                                                        </Box>
                                                    ) : (
                                                        <>
                                                            <Autocomplete
                                                                freeSolo
                                                                options={contacts}
                                                                getOptionLabel={(option) =>
                                                                    typeof option === 'string' ? option : option.name
                                                                }
                                                                value={contacts.find(c => c.name === formData.auftraggeber_name) || formData.auftraggeber_name || ''}
                                                                onChange={(_, newValue) => {
                                                                    if (newValue === null) {
                                                                        setFormData({...formData, auftraggeber_name: ''});
                                                                    } else if (typeof newValue === 'string') {
                                                                        setFormData({...formData, auftraggeber_name: newValue});
                                                                    } else {
                                                                        handleAuftraggeberContactSelectStructured(newValue);
                                                                    }
                                                                }}
                                                                onInputChange={(_, newInputValue, reason) => {
                                                                    if (reason === 'input') {
                                                                        setFormData({...formData, auftraggeber_name: newInputValue});
                                                                    }
                                                                }}
                                                                size="small"
                                                                renderInput={(params) => (
                                                                    <TextField
                                                                        {...params}
                                                                        label="Name"
                                                                        placeholder="Kontakt auswählen oder eingeben..."
                                                                        variant="outlined"
                                                                    />
                                                                )}
                                                                renderOption={(props, option) => (
                                                                    <li {...props} key={option.id}>
                                                                        <Box>
                                                                            <Typography variant="body2">{option.name}</Typography>
                                                                            {option.email && (
                                                                                <Typography variant="caption" color="text.secondary">
                                                                                    {option.email}
                                                                                </Typography>
                                                                            )}
                                                                        </Box>
                                                                    </li>
                                                                )}
                                                            />
                                                            <TextField
                                                                fullWidth
                                                                size='small'
                                                                label='E-Mail'
                                                                type='email'
                                                                value={formData.auftraggeber_email || ''}
                                                                onChange={(e) =>
                                                                    setFormData({...formData, auftraggeber_email: e.target.value})
                                                                }
                                                                variant='outlined'
                                                            />
                                                            <TextField
                                                                fullWidth
                                                                size='small'
                                                                label='Telefon'
                                                                value={formData.auftraggeber_phone || ''}
                                                                onChange={(e) =>
                                                                    setFormData({...formData, auftraggeber_phone: e.target.value})
                                                                }
                                                                variant='outlined'
                                                            />
                                                            <TextField
                                                                fullWidth
                                                                size='small'
                                                                label='Straße & Hausnr.'
                                                                value={formData.auftraggeber_street || ''}
                                                                onChange={(e) =>
                                                                    setFormData({...formData, auftraggeber_street: e.target.value})
                                                                }
                                                                variant='outlined'
                                                            />
                                                            <TextField
                                                                fullWidth
                                                                size='small'
                                                                label='Adresszusatz'
                                                                value={formData.auftraggeber_additional_street || ''}
                                                                onChange={(e) =>
                                                                    setFormData({...formData, auftraggeber_additional_street: e.target.value})
                                                                }
                                                                variant='outlined'
                                                            />
                                                            <Box sx={{display: 'flex', gap: 2}}>
                                                                <TextField
                                                                    size='small'
                                                                    label='PLZ'
                                                                    value={formData.auftraggeber_postal_code || ''}
                                                                    onChange={(e) =>
                                                                        setFormData({...formData, auftraggeber_postal_code: e.target.value})
                                                                    }
                                                                    variant='outlined'
                                                                    sx={{width: '30%'}}
                                                                />
                                                                <TextField
                                                                    size='small'
                                                                    label='Stadt'
                                                                    value={formData.auftraggeber_city || ''}
                                                                    onChange={(e) =>
                                                                        setFormData({...formData, auftraggeber_city: e.target.value})
                                                                    }
                                                                    variant='outlined'
                                                                    sx={{flex: 1}}
                                                                />
                                                            </Box>
                                                        </>
                                                    )}
                                                </Stack>
                                            </Box>
                                        </Grid>

                                        {/* Terminvereinbarung - Right Column */}
                                        <Grid item xs={12} md={4}>
                                            <Box sx={{
                                                p: 2,
                                                border: '1px solid',
                                                borderColor: 'divider',
                                                borderRadius: 2,
                                                height: '100%',
                                            }}>
                                                <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2}}>
                                                    <Typography variant='subtitle1' fontWeight={600}>
                                                        Terminvereinbarung
                                                    </Typography>
                                                    <FormControlLabel
                                                        control={
                                                            <Checkbox
                                                                size='small'
                                                                checked={!formData.appointment_use_full_contact}
                                                                onChange={(e) => {
                                                                    const useStructured = e.target.checked;
                                                                    if (!useStructured && !formData.appointment_use_full_contact) {
                                                                        // Switching to textarea mode - auto-format existing data
                                                                        const parts = [
                                                                            formData.appointment_name,
                                                                            formData.appointment_email,
                                                                            formData.appointment_phone,
                                                                            formData.appointment_street,
                                                                            formData.appointment_additional_street,
                                                                            [formData.appointment_postal_code, formData.appointment_city].filter(Boolean).join(' ')
                                                                        ].filter(Boolean);
                                                                        setFormData({
                                                                            ...formData,
                                                                            appointment_use_full_contact: true,
                                                                            appointment_full_contact: parts.join('\n') || formData.appointment_full_contact
                                                                        });
                                                                    } else {
                                                                        setFormData({...formData, appointment_use_full_contact: !useStructured});
                                                                    }
                                                                }}
                                                            />
                                                        }
                                                        label={<Typography variant='body2'>Einzelfelder</Typography>}
                                                        sx={{mr: 0}}
                                                    />
                                                </Box>
                                                <Stack spacing={2}>
                                                    {formData.appointment_use_full_contact ? (
                                                        <Box>
                                                            <Autocomplete
                                                                options={contacts}
                                                                getOptionLabel={(option) => option.name}
                                                                onChange={(_, newValue) => handleAppointmentContactSelect(newValue)}
                                                                size="small"
                                                                renderInput={(params) => (
                                                                    <TextField
                                                                        {...params}
                                                                        label="Kontakt suchen"
                                                                        placeholder="Kontakt auswählen..."
                                                                        size="small"
                                                                        sx={{mb: 1}}
                                                                    />
                                                                )}
                                                                renderOption={(props, option) => (
                                                                    <li {...props} key={option.id}>
                                                                        <Box>
                                                                            <Typography variant="body2">{option.name}</Typography>
                                                                            {option.email && (
                                                                                <Typography variant="caption" color="text.secondary">
                                                                                    {option.email}
                                                                                </Typography>
                                                                            )}
                                                                        </Box>
                                                                    </li>
                                                                )}
                                                            />
                                                            <TextField
                                                                fullWidth
                                                                size='small'
                                                                label='Kontaktdaten'
                                                                value={formData.appointment_full_contact || ''}
                                                                onChange={(e) =>
                                                                    setFormData({...formData, appointment_full_contact: e.target.value})
                                                                }
                                                                variant='outlined'
                                                                multiline
                                                                rows={5}
                                                                placeholder={'Name\nE-Mail\nTelefon\nAdresse'}
                                                            />
                                                        </Box>
                                                    ) : (
                                                        <>
                                                            <Autocomplete
                                                                freeSolo
                                                                options={contacts}
                                                                getOptionLabel={(option) =>
                                                                    typeof option === 'string' ? option : option.name
                                                                }
                                                                value={contacts.find(c => c.name === formData.appointment_name) || formData.appointment_name || ''}
                                                                onChange={(_, newValue) => {
                                                                    if (newValue === null) {
                                                                        setFormData({...formData, appointment_name: ''});
                                                                    } else if (typeof newValue === 'string') {
                                                                        setFormData({...formData, appointment_name: newValue});
                                                                    } else {
                                                                        handleAppointmentContactSelectStructured(newValue);
                                                                    }
                                                                }}
                                                                onInputChange={(_, newInputValue, reason) => {
                                                                    if (reason === 'input') {
                                                                        setFormData({...formData, appointment_name: newInputValue});
                                                                    }
                                                                }}
                                                                size="small"
                                                                renderInput={(params) => (
                                                                    <TextField
                                                                        {...params}
                                                                        label="Name"
                                                                        placeholder="Kontakt auswählen oder eingeben..."
                                                                        variant="outlined"
                                                                    />
                                                                )}
                                                                renderOption={(props, option) => (
                                                                    <li {...props} key={option.id}>
                                                                        <Box>
                                                                            <Typography variant="body2">{option.name}</Typography>
                                                                            {option.email && (
                                                                                <Typography variant="caption" color="text.secondary">
                                                                                    {option.email}
                                                                                </Typography>
                                                                            )}
                                                                        </Box>
                                                                    </li>
                                                                )}
                                                            />
                                                            <TextField
                                                                fullWidth
                                                                size='small'
                                                                label='E-Mail'
                                                                type='email'
                                                                value={formData.appointment_email || ''}
                                                                onChange={(e) =>
                                                                    setFormData({...formData, appointment_email: e.target.value})
                                                                }
                                                                variant='outlined'
                                                            />
                                                            <TextField
                                                                fullWidth
                                                                size='small'
                                                                label='Telefon'
                                                                value={formData.appointment_phone || ''}
                                                                onChange={(e) =>
                                                                    setFormData({...formData, appointment_phone: e.target.value})
                                                                }
                                                                variant='outlined'
                                                            />
                                                            <TextField
                                                                fullWidth
                                                                size='small'
                                                                label='Straße & Hausnr.'
                                                                value={formData.appointment_street || ''}
                                                                onChange={(e) =>
                                                                    setFormData({...formData, appointment_street: e.target.value})
                                                                }
                                                                variant='outlined'
                                                            />
                                                            <TextField
                                                                fullWidth
                                                                size='small'
                                                                label='Adresszusatz'
                                                                value={formData.appointment_additional_street || ''}
                                                                onChange={(e) =>
                                                                    setFormData({...formData, appointment_additional_street: e.target.value})
                                                                }
                                                                variant='outlined'
                                                            />
                                                            <Box sx={{display: 'flex', gap: 2}}>
                                                                <TextField
                                                                    size='small'
                                                                    label='PLZ'
                                                                    value={formData.appointment_postal_code || ''}
                                                                    onChange={(e) =>
                                                                        setFormData({...formData, appointment_postal_code: e.target.value})
                                                                    }
                                                                    variant='outlined'
                                                                    sx={{width: '30%'}}
                                                                />
                                                                <TextField
                                                                    size='small'
                                                                    label='Stadt'
                                                                    value={formData.appointment_city || ''}
                                                                    onChange={(e) =>
                                                                        setFormData({...formData, appointment_city: e.target.value})
                                                                    }
                                                                    variant='outlined'
                                                                    sx={{flex: 1}}
                                                                />
                                                            </Box>
                                                        </>
                                                    )}
                                                </Stack>
                                            </Box>
                                        </Grid>

                                        {/* Rechnungsempfänger - Third Column */}
                                        <Grid item xs={12} md={4}>
                                            <Box sx={{
                                                p: 2,
                                                border: '1px solid',
                                                borderColor: 'divider',
                                                borderRadius: 2,
                                                height: '100%',
                                            }}>
                                                <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2}}>
                                                    <Typography variant='subtitle1' fontWeight={600}>
                                                        Rechnungsempfänger
                                                    </Typography>
                                                    <FormControlLabel
                                                        control={
                                                            <Checkbox
                                                                size='small'
                                                                checked={!formData.invoice_use_full_contact}
                                                                onChange={(e) => {
                                                                    const useStructured = e.target.checked;
                                                                    if (!useStructured && !formData.invoice_use_full_contact) {
                                                                        // Switching to textarea mode - auto-format existing data
                                                                        const parts = [
                                                                            formData.invoice_name,
                                                                            formData.invoice_email,
                                                                            formData.invoice_phone,
                                                                            formData.invoice_street,
                                                                            formData.invoice_additional_street,
                                                                            [formData.invoice_postal_code, formData.invoice_city].filter(Boolean).join(' ')
                                                                        ].filter(Boolean);
                                                                        setFormData({
                                                                            ...formData,
                                                                            invoice_use_full_contact: true,
                                                                            invoice_full_contact: parts.join('\n') || formData.invoice_full_contact
                                                                        });
                                                                    } else {
                                                                        setFormData({...formData, invoice_use_full_contact: !useStructured});
                                                                    }
                                                                }}
                                                            />
                                                        }
                                                        label={<Typography variant='body2'>Einzelfelder</Typography>}
                                                        sx={{mr: 0}}
                                                    />
                                                </Box>
                                                <Stack spacing={2}>
                                                    {formData.invoice_use_full_contact ? (
                                                        <Box>
                                                            <Autocomplete
                                                                options={contacts}
                                                                getOptionLabel={(option) => option.name}
                                                                onChange={(_, newValue) => handleInvoiceContactSelect(newValue)}
                                                                size="small"
                                                                renderInput={(params) => (
                                                                    <TextField
                                                                        {...params}
                                                                        label="Kontakt suchen"
                                                                        placeholder="Kontakt auswählen..."
                                                                        size="small"
                                                                        sx={{mb: 1}}
                                                                    />
                                                                )}
                                                                renderOption={(props, option) => (
                                                                    <li {...props} key={option.id}>
                                                                        <Box>
                                                                            <Typography variant="body2">{option.name}</Typography>
                                                                            {option.email && (
                                                                                <Typography variant="caption" color="text.secondary">
                                                                                    {option.email}
                                                                                </Typography>
                                                                            )}
                                                                        </Box>
                                                                    </li>
                                                                )}
                                                            />
                                                            <TextField
                                                                fullWidth
                                                                size='small'
                                                                label='Kontaktdaten'
                                                                value={formData.invoice_full_contact || ''}
                                                                onChange={(e) =>
                                                                    setFormData({...formData, invoice_full_contact: e.target.value})
                                                                }
                                                                variant='outlined'
                                                                multiline
                                                                rows={5}
                                                                placeholder={'Name\nE-Mail\nTelefon\nAdresse'}
                                                            />
                                                        </Box>
                                                    ) : (
                                                        <>
                                                            <Autocomplete
                                                                freeSolo
                                                                options={contacts}
                                                                getOptionLabel={(option) =>
                                                                    typeof option === 'string' ? option : option.name
                                                                }
                                                                value={contacts.find(c => c.name === formData.invoice_name) || formData.invoice_name || ''}
                                                                onChange={(_, newValue) => {
                                                                    if (newValue === null) {
                                                                        setFormData({...formData, invoice_name: ''});
                                                                    } else if (typeof newValue === 'string') {
                                                                        setFormData({...formData, invoice_name: newValue});
                                                                    } else {
                                                                        handleInvoiceContactSelectStructured(newValue);
                                                                    }
                                                                }}
                                                                onInputChange={(_, newInputValue, reason) => {
                                                                    if (reason === 'input') {
                                                                        setFormData({...formData, invoice_name: newInputValue});
                                                                    }
                                                                }}
                                                                size="small"
                                                                renderInput={(params) => (
                                                                    <TextField
                                                                        {...params}
                                                                        label="Name"
                                                                        placeholder="Kontakt auswählen oder eingeben..."
                                                                        variant="outlined"
                                                                    />
                                                                )}
                                                                renderOption={(props, option) => (
                                                                    <li {...props} key={option.id}>
                                                                        <Box>
                                                                            <Typography variant="body2">{option.name}</Typography>
                                                                            {option.email && (
                                                                                <Typography variant="caption" color="text.secondary">
                                                                                    {option.email}
                                                                                </Typography>
                                                                            )}
                                                                        </Box>
                                                                    </li>
                                                                )}
                                                            />
                                                            <TextField
                                                                fullWidth
                                                                size='small'
                                                                label='E-Mail'
                                                                type='email'
                                                                value={formData.invoice_email || ''}
                                                                onChange={(e) =>
                                                                    setFormData({...formData, invoice_email: e.target.value})
                                                                }
                                                                variant='outlined'
                                                            />
                                                            <TextField
                                                                fullWidth
                                                                size='small'
                                                                label='Telefon'
                                                                value={formData.invoice_phone || ''}
                                                                onChange={(e) =>
                                                                    setFormData({...formData, invoice_phone: e.target.value})
                                                                }
                                                                variant='outlined'
                                                            />
                                                            <TextField
                                                                fullWidth
                                                                size='small'
                                                                label='Straße & Hausnr.'
                                                                value={formData.invoice_street || ''}
                                                                onChange={(e) =>
                                                                    setFormData({...formData, invoice_street: e.target.value})
                                                                }
                                                                variant='outlined'
                                                            />
                                                            <TextField
                                                                fullWidth
                                                                size='small'
                                                                label='Adresszusatz'
                                                                value={formData.invoice_additional_street || ''}
                                                                onChange={(e) =>
                                                                    setFormData({...formData, invoice_additional_street: e.target.value})
                                                                }
                                                                variant='outlined'
                                                            />
                                                            <Box sx={{display: 'flex', gap: 2}}>
                                                                <TextField
                                                                    size='small'
                                                                    label='PLZ'
                                                                    value={formData.invoice_postal_code || ''}
                                                                    onChange={(e) =>
                                                                        setFormData({...formData, invoice_postal_code: e.target.value})
                                                                    }
                                                                    variant='outlined'
                                                                    sx={{width: '30%'}}
                                                                />
                                                                <TextField
                                                                    size='small'
                                                                    label='Stadt'
                                                                    value={formData.invoice_city || ''}
                                                                    onChange={(e) =>
                                                                        setFormData({...formData, invoice_city: e.target.value})
                                                                    }
                                                                    variant='outlined'
                                                                    sx={{flex: 1}}
                                                                />
                                                            </Box>
                                                        </>
                                                    )}
                                                </Stack>
                                            </Box>
                                        </Grid>
                                    </Grid>
                                </CardContent>
                            </Card>

                            {/* Description Card */}
                            <Card variant='outlined' sx={{mb: 3}}>
                                <CardContent>
                                    <Box display='flex' alignItems='center' gap={1} mb={2}>
                                        <DescriptionIcon color='primary'/>
                                        <Typography variant='h6'>Beschreibung</Typography>
                                    </Box>
                                    <TextField
                                        fullWidth
                                        multiline
                                        rows={4}
                                        value={formData.description}
                                        onChange={(e) => setFormData({...formData, description: e.target.value})}
                                        placeholder='Beschreibung der Aufgabe...'
                                    />
                                </CardContent>
                            </Card>

                            {/* Attachments Card */}
                            <Card
                                variant='outlined'
                                sx={{
                                    mb: 3,
                                    borderRadius: 3,
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                                    '&:hover': {
                                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                                    },
                                    transition: 'all 0.2s ease-in-out',
                                }}
                            >
                                <CardContent sx={{p: 3}}>
                                    <Box display='flex' alignItems='center' gap={1.5} mb={3}>
                                        <Box
                                            sx={{
                                                width: 40,
                                                height: 40,
                                                borderRadius: 2,
                                                backgroundColor: 'primary.50',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                            }}
                                        >
                                            <AttachFileIcon sx={{color: 'primary.main', fontSize: 20}}/>
                                        </Box>
                                        <Box>
                                            <Typography variant='h6' fontWeight={600} color='text.primary'>
                                                Anhänge
                                            </Typography>
                                            <Typography variant='body2' color='text.secondary'>
                                                {selectedImages.length + selectedDocuments.length} {selectedImages.length + selectedDocuments.length === 1 ? 'Datei' : 'Dateien'} ausgewählt
                                            </Typography>
                                        </Box>
                                    </Box>

                                    {/* Upload Section */}
                                    <Box sx={{mb: 3}}>
                                        <HiddenInput
                                            ref={fileInputRef}
                                            type='file'
                                            accept='image/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.json,.xml,.zip,.rar,.7z'
                                            onChange={handleFileUpload}
                                            multiple
                                        />
                                        <Button
                                            variant='outlined'
                                            startIcon={
                                                uploadingImages || uploadingDocuments ? (
                                                    <CircularProgress size={18} color='inherit'/>
                                                ) : (
                                                    <CloudUploadIcon/>
                                                )
                                            }
                                            onClick={() => fileInputRef.current?.click()}
                                            disabled={uploadingImages || uploadingDocuments}
                                            sx={{
                                                borderRadius: 3,
                                                textTransform: 'none',
                                                fontWeight: 600,
                                                px: 3,
                                                py: 1.5,
                                                boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                                                border: '2px solid',
                                                borderColor: 'primary.main',
                                                color: 'primary.main',
                                                backgroundColor: 'transparent',
                                                '&:hover': {
                                                    backgroundColor: 'primary.50',
                                                    boxShadow: '0 4px 12px rgba(0,0,0,0.25)',
                                                    transform: 'translateY(-1px)',
                                                    borderColor: 'primary.dark',
                                                },
                                                '&:disabled': {
                                                    boxShadow: 'none',
                                                    backgroundColor: 'action.disabledBackground',
                                                    borderColor: 'action.disabled',
                                                    color: 'action.disabled',
                                                },
                                                transition: 'all 0.2s ease-in-out',
                                            }}>
                                            {uploadingImages || uploadingDocuments
                                                ? 'Wird hochgeladen...'
                                                : 'Dateien hinzufügen'}
                                        </Button>
                                    </Box>

                                    {selectedImages.length > 0 || selectedDocuments.length > 0 ? (
                                        <AttachmentList
                                            items={[
                                                ...selectedImages.map((image, index) => ({
                                                    id: index,
                                                    type: 'image' as const,
                                                    name: image.name,
                                                    original_filename: image.name,
                                                    filename: image.name,
                                                    size: image.size,
                                                    uploaded_at: new Date().toISOString(),
                                                })),
                                                ...selectedDocuments.map((document, index) => ({
                                                    id: selectedImages.length + index,
                                                    type: 'document' as const,
                                                    name: document.name,
                                                    filename: document.name,
                                                    mime_type: document.type,
                                                    size: document.size,
                                                    file_size: document.size,
                                                    uploaded_at: new Date().toISOString(),
                                                })),
                                            ]}
                                            getDocumentIcon={(mimeType, filename) =>
                                                getDocumentIcon(mimeType || '', filename || '')
                                            }
                                            formatDate={(dateString) => new Date(dateString).toLocaleDateString('de-DE')}
                                            onImageClick={(item) => {
                                                const index = item.id as number;
                                                if (selectedImages[index]) {
                                                    handleImageClick(selectedImages[index], index);
                                                }
                                            }}
                                            onEditImage={(item) => {
                                                const index = item.id as number;
                                                if (selectedImages[index]) {
                                                    handleEditImage(index);
                                                }
                                            }}
                                            onCropImage={(item) => {
                                                const index = item.id as number;
                                                if (selectedImages[index]) {
                                                    handleCropImage(index);
                                                }
                                            }}
                                            onDownloadImage={(item) => {
                                                const index = item.id as number;
                                                if (selectedImages[index]) {
                                                    handleDownloadImage(selectedImages[index], index);
                                                }
                                            }}
                                            onDeleteImage={(id) => {
                                                const index = id as number;
                                                if (index < selectedImages.length) {
                                                    handleRemoveImage(index);
                                                }
                                            }}
                                            onDownloadDocument={(item) => {
                                                const index = (item.id as number) - selectedImages.length;
                                                if (index >= 0 && selectedDocuments[index]) {
                                                    handleDocumentClick(selectedDocuments[index]);
                                                }
                                            }}
                                            onDeleteDocument={(id) => {
                                                const index = (id as number) - selectedImages.length;
                                                if (index >= 0) {
                                                    handleRemoveDocument(index);
                                                }
                                            }}
                                            showEditActions={true}
                                            showCropActions={true}
                                        />
                                    ) : (
                                        <Box
                                            sx={{
                                                textAlign: 'center',
                                                py: 6,
                                                px: 4,
                                                bgcolor: 'grey.50',
                                                border: '2px dashed',
                                                borderColor: 'grey.300',
                                                borderRadius: 3,
                                                color: 'text.secondary',
                                                transition: 'all 0.2s ease-in-out',
                                                cursor: 'pointer',
                                                '&:hover': {
                                                    borderColor: 'primary.main',
                                                    bgcolor: 'primary.50',
                                                },
                                            }}
                                            onClick={() => fileInputRef.current?.click()}
                                        >
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    alignItems: 'center',
                                                    gap: 2,
                                                }}
                                            >
                                                <Box
                                                    sx={{
                                                        width: 64,
                                                        height: 64,
                                                        borderRadius: '50%',
                                                        backgroundColor: 'grey.200',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        mb: 1,
                                                    }}
                                                >
                                                    <AttachFileIcon sx={{fontSize: 32, color: 'grey.500'}}/>
                                                </Box>
                                                <Typography variant='h6' fontWeight={600} color='text.primary'>
                                                    Keine Anhänge ausgewählt
                                                </Typography>
                                                <Typography variant='body2' sx={{maxWidth: 300}}>
                                                    Laden Sie Bilder oder Dokumente hoch, um sie mit der Aufgabe zu verknüpfen.
                                                </Typography>
                                                <Button
                                                    variant='outlined'
                                                    startIcon={<CloudUploadIcon/>}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        fileInputRef.current?.click();
                                                    }}
                                                    sx={{
                                                        borderRadius: 2,
                                                        textTransform: 'none',
                                                        fontWeight: 600,
                                                        px: 3,
                                                        py: 1,
                                                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                                                        '&:hover': {
                                                            boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
                                                            transform: 'translateY(-1px)',
                                                        },
                                                        transition: 'all 0.2s ease-in-out',
                                                    }}
                                                >
                                                    Dateien auswählen
                                                </Button>
                                            </Box>
                                        </Box>
                                    )}
                                </CardContent>
                            </Card>

                            {/* Action Buttons Section - Similar to task-detail.tsx */}
                            <Box sx={{
                                mt: 4,
                                pt: 3,
                                borderTop: '1px solid',
                                borderColor: 'divider',
                                display: 'flex',
                                justifyContent: 'flex-end',
                                flexWrap: 'wrap',
                                alignItems: 'center',
                                gap: 2,
                                backgroundColor: 'transparent',
                            }}>
                                {/* Left Side - Cancel Button */}
                                <Button
                                    onClick={onClose}
                                    variant='outlined'
                                    sx={{
                                        borderRadius: 2,
                                        textTransform: 'none',
                                        fontWeight: 500,
                                        px: 4,
                                        py: 1.5,
                                        color: 'text.secondary',
                                        borderColor: 'divider',
                                        '&:hover': {
                                            borderColor: 'primary.main',
                                            backgroundColor: 'action.hover',
                                            color: 'text.primary',
                                        }
                                    }}
                                >
                                    Schließen
                                </Button>

                                {/* Right Side - Create Button */}
                                <Button
                                    onClick={handleSubmit}
                                    variant='contained'
                                    color='success'
                                    disabled={loading || loadingData || uploadingImages || uploadingDocuments}
                                    sx={{
                                        borderRadius: 2,
                                        textTransform: 'none',
                                        fontWeight: 600,
                                        px: 4,
                                        py: 1.5,
                                        boxShadow: '0 4px 12px rgba(46, 125, 50, 0.3)',
                                        '&:hover': {
                                            boxShadow: '0 6px 16px rgba(46, 125, 50, 0.4)',
                                            transform: 'translateY(-1px)',
                                        },
                                        '&:disabled': {
                                            boxShadow: 'none',
                                            backgroundColor: 'action.disabledBackground',
                                        },
                                        transition: 'all 0.2s ease-in-out',
                                    }}
                                >
                                    {loading || uploadingImages || uploadingDocuments
                                        ? <><CircularProgress size={20} sx={{color: 'inherit', mr: 1}} /> Wird erstellt...</>
                                        : 'Aufgabe erstellen'}
                                </Button>
                            </Box>
                        </Box>
                    )}
                </DialogContent>
            </Dialog>

            {/* Image Crop Dialog */}
            {croppingImageFile && (
                <ImageCrop
                    imageFile={croppingImageFile}
                    onSave={handleSaveCroppedImage}
                    onCancel={() => {
                        setCroppingImageIndex(null);
                        setCroppingImageFile(null);
                    }}
                    isOpen={croppingImageIndex !== null}
                />
            )}

            {/* Reset Warning Dialog */}
            <Dialog
                open={resetWarningOpen}
                onClose={handleCancelReset}
                maxWidth='sm'
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: 3,
                        boxShadow: '0 16px 48px rgba(0,0,0,0.15)',
                    },
                }}
            >
                <DialogTitle sx={{pb: 2}}>
                    <Box display='flex' alignItems='center' gap={1.5}>
                        <Box
                            sx={{
                                width: 40,
                                height: 40,
                                borderRadius: 2,
                                backgroundColor: 'warning.50',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                        >
                            <WarningIcon sx={{color: 'warning.main', fontSize: 20}}/>
                        </Box>
                        <Typography variant='h6' fontWeight={600}>
                            Bildbearbeitung zurücksetzen?
                        </Typography>
                    </Box>
                </DialogTitle>
                <DialogContent sx={{pb: 2}}>
                    <Typography variant='body1' sx={{mb: 2}} color='text.secondary'>
                        Dieses Bild wurde bereits bearbeitet. Wenn Sie fortfahren, werden alle vorherigen
                        Bearbeitungen entfernt und Sie starten mit dem ursprünglichen Bild.
                    </Typography>
                    <Alert
                        severity='warning'
                        sx={{
                            mt: 2,
                            borderRadius: 2,
                            '& .MuiAlert-icon': {
                                fontSize: 20,
                            },
                        }}
                    >
                        <Typography variant='body2' fontWeight={500}>
                            <strong>Achtung:</strong> Diese Aktion kann nicht rückgängig gemacht werden.
                        </Typography>
                    </Alert>
                </DialogContent>
                <DialogActions sx={{px: 3, pb: 3, pt: 1, flex: 1, flexWrap: 'wrap'}}>
                    <Button
                        onClick={handleCancelReset}
                        color='inherit'
                        sx={{
                            borderRadius: 2,
                            textTransform: 'none',
                            fontWeight: 500,
                            px: 3,
                        }}
                    >
                        Abbrechen
                    </Button>
                    <Button
                        onClick={handleConfirmReset}
                        variant='outlined'
                        color='warning'
                        startIcon={<WarningIcon/>}
                        sx={{
                            borderRadius: 2,
                            textTransform: 'none',
                            fontWeight: 600,
                            px: 3,
                            boxShadow: '0 2px 8px rgba(255, 152, 0, 0.2)',
                            '&:hover': {
                                boxShadow: '0 4px 12px rgba(255, 152, 0, 0.3)',
                                transform: 'translateY(-1px)',
                            },
                            transition: 'all 0.2s ease-in-out',
                        }}
                    >
                        Zurücksetzen und neu bearbeiten
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Image Preview Dialog */}
            <Dialog
                open={Boolean(selectedImage)}
                onClose={() => {
                    setSelectedImage(null);
                    setSelectedImageIndex(null);
                }}
                maxWidth={false}
                fullWidth
                PaperProps={{
                    sx: {
                        height: {xs: '100vh', sm: '90vh'},
                        maxHeight: {xs: '100vh', sm: '90vh'},
                        width: {xs: '100vw', sm: '90vw', md: '80vw', lg: '70vw'},
                        maxWidth: {xs: '100vw', sm: '90vw', md: '80vw', lg: '70vw'},
                        margin: {xs: 0, sm: 'auto'},
                        borderRadius: 3,
                        boxShadow: '0 16px 48px rgba(0,0,0,0.15)',
                    },
                }}
            >
                <DialogTitle sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    pb: 1,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                }}>
                    <Box display='flex' alignItems='center' gap={2}>
                        <Typography variant='h6' fontWeight={600}>
                            Bildvorschau
                        </Typography>
                        {selectedImageIndex !== null && editedImages.has(selectedImageIndex) && (
                            <Chip
                                label='Bearbeitet'
                                size='small'
                                color='primary'
                                sx={{
                                    fontWeight: 600,
                                    borderRadius: 2,
                                }}
                            />
                        )}
                    </Box>
                    <IconButton
                        onClick={() => {
                            setSelectedImage(null);
                            setSelectedImageIndex(null);
                        }}
                        sx={{
                            color: 'text.secondary',
                            '&:hover': {
                                backgroundColor: 'action.hover',
                                color: 'primary.main',
                            },
                        }}
                    >
                        <CloseIcon/>
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{
                    p: 3,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: 'calc(100% - 64px)',
                    overflow: 'hidden',
                }}>
                    {selectedImage && (
                        <Box
                            component='img'
                            src={URL.createObjectURL(selectedImage)}
                            alt='Vorschau'
                            sx={{
                                maxWidth: '100%',
                                maxHeight: '100%',
                                objectFit: 'contain',
                                borderRadius: 2,
                                boxShadow: '0 8px 24px rgba(0,0,0,0.1)',
                            }}
                        />
                    )}
                </DialogContent>
            </Dialog>

            {/* Image Edit Dialog */}
            <Dialog
                open={editingImageIndex !== null}
                onClose={() => setEditingImageIndex(null)}
                maxWidth={false}
                fullWidth
                fullScreen={false} // Kein Fullscreen wie im ImageEditModal
                PaperProps={{
                    className: 'modal-dialog',
                    sx: {
                        height: {xs: '100vh', sm: '90vh'}, // Mobile: Vollbild, Desktop: 90%
                        maxHeight: {xs: '100vh', sm: '90vh'},
                        width: {xs: '100vw', sm: '90vw', md: '80vw', lg: '70vw'},
                        maxWidth: {xs: '100vw', sm: '90vw', md: '80vw', lg: '70vw'},
                        margin: {xs: 0, sm: 'auto'},
                        borderRadius: 3,
                        boxShadow: '0 16px 48px rgba(0,0,0,0.15)',
                        backgroundImage: 'linear-gradient(180deg, #ffffff 0%, #fafafa 100%)',
                    },
                }}
            >
                <DialogTitle sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    pb: 1,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                }}>
                    <Typography variant='h6' fontWeight={600} color='text.primary'>
                        Bild bearbeiten
                    </Typography>
                    <IconButton
                        onClick={() => setEditingImageIndex(null)}
                        sx={{
                            color: 'text.secondary',
                            '&:hover': {
                                backgroundColor: 'action.hover',
                                color: 'primary.main',
                            },
                        }}
                    >
                        <CloseIcon/>
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{
                    p: 0,
                    height: 'calc(100% - 64px)',
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column',
                    backgroundColor: '#fafafa'
                }}>
                    {editingImageIndex !== null && (
                        <TaskImageEdit
                            imageFile={getImageForEditing(editingImageIndex)}
                            onSave={handleSaveEditedImage}
                            onCancel={() => setEditingImageIndex(null)}
                            maxWidth={1000}
                            maxHeight={600}
                            inModal={true}
                        />
                    )}
                    {editingImageIndex === null && (
                        <div style={{padding: '20px', textAlign: 'center'}}>
                            Bild wird geladen...
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </>
    );
}
